"use client"
import React, { useState, useMemo, useCallback } from "react"
import { useSelector, shallowEqual } from "react-redux"
import {
  Avatar,
  Container,
  Typography,
  Box,
  Card,
  Button,
  Stack,
  Chip,
  Divider,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material"
import { Grid } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Sector
} from "recharts"
import { PieChart } from '@mui/x-charts/PieChart'
import { useEffectAsync } from "../reactHelper"
import { useTheme } from "@mui/material/styles"
import PageLayout from "../common/components/PageLayout"
import SettingsMenu from "./components/SettingsMenu"
import useCrudExports from '../common/hooks/useCrudExports'
import { sessionActions } from "../store"
import LogoImage from "../resources/images/logo.svg?react"
import { nativePostMessage } from "../common/components/NativeInterface"
import { jsPDF } from "jspdf"
import "jspdf-autotable"

// Icons
import Diversity3Icon from "@mui/icons-material/Diversity3"
import GroupIcon from "@mui/icons-material/Group"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import DevicesIcon from "@mui/icons-material/Devices"
import TerrainIcon from "@mui/icons-material/Terrain"
import ErrorIcon from "@mui/icons-material/Error"
import HelpIcon from "@mui/icons-material/Help"
import LockIcon from "@mui/icons-material/Lock"
import LockOpenIcon from '@mui/icons-material/LockOpen'
import SearchIcon from '@mui/icons-material/Search'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import TableChartIcon from '@mui/icons-material/TableChart';
import StorageIcon from '@mui/icons-material/Storage'

// Utilities and Hooks
import { useTranslation } from "../common/components/LocalizationProvider"
import { useAdministrator, useManager } from "../common/util/permissions"
import { formatBoolean, formatDate, getStatusText, formatAlarm } from "../common/util/formatter"
import { mapIconKey, mapIcons } from "../map/core/preloadImages"
import { usePreference } from "../common/util/preferences"
import useSettingsStyles from "./common/useSettingsStyles"
import fetchOrThrow from "../common/util/fetchOrThrow"

const PrintHeader = () => {
  const t = useTranslation()
  const currentUser = useSelector((state) => state.session.user)

  const printStyles = {
    container: {
      padding: 10,
      color: "#000",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
    },
    title: {
      fontSize: 24,
      margin: 0,
      color: "#000",
    },
    infoSection: {
      marginTop: 10,
      fontSize: 14,
      color: "#333",
    },
    infoLabel: {
      fontWeight: "bold",
      color: "#000",
    },
  }

  return (
    <div style={printStyles.container}>
      <div style={printStyles.header}>
        <LogoImage
          style={{ height: 60 }}
          onLoad={async () => {
            requestAnimationFrame(() =>
              requestAnimationFrame(() => {
                window.print()
                nativePostMessage(`print|${t("dashboardMenuText")}.pdf`)
              }),
            )
          }}
          alt={t("companyLogo")}
        />
        <h1 style={printStyles.title}>{t("dashboardMenuText")}</h1>
      </div>
      <div style={printStyles.infoSection}>
        <div>
          <strong style={printStyles.infoLabel}>{t("shared.user")}:</strong> {currentUser?.name || ""}
        </div>
        <div>
          <strong style={printStyles.infoLabel}>{t("reportDate")}:</strong> {formatDate(new Date().toISOString())}
        </div>
      </div>
    </div>
  )
}

const CustomPieLegend = ({ data, theme }) => {
  const t = useTranslation()
  const total = data.reduce((sum, item) => sum + item.value, 0)

  return (
    <Grid container spacing={1} justifyContent="center" sx={{ mt: 2 }}>
      {data.map((item, index) => {
        const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0
        const color = [
          theme.palette.success.main,
          theme.palette.error.main,
          theme.palette.warning.main
        ][index]

        return (
          <Grid key={index} size={{ xs: 12, sm: 4 }} sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{
                width: 16,
                height: 16,
                bgcolor: color,
                mr: 1,
                borderRadius: '4px'
              }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {item.name}:
              </Typography>
              <Typography variant="body2" sx={{ ml: 0.5 }}>
                {item.value} ({percentage}%)
              </Typography>
            </Box>
          </Grid>
        )
      })}
      <Grid size={12} sx={{ textAlign: 'center', ml: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t('deviceStatusTotal')}: {total}
        </Typography>
      </Grid>
    </Grid>
  )
}

const CustomTooltip = ({ active, payload, label, theme }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[3],
      }}>
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Typography
            key={index}
            variant="body2"
            style={{ color: entry.color, display: 'block' }}
          >
            {`${entry.name}: ${entry.value}`}
          </Typography>
        ))}
      </div>
    )
  }
  return null
}

const DashboardPage = () => {
  const theme = useTheme()
  const t = useTranslation()
  const settingsClasses = useSettingsStyles()

  const admin = useAdministrator()
  const manager = useManager()

  const userLimitCommands = usePreference("limitCommands")
  const userDeviceReadonly = usePreference("deviceReadonly")
  const userReadonly = usePreference("readonly")

  const positions = useSelector((state) => Object.values(state.positions.items || {}), shallowEqual)
  const groups = useSelector((state) => Object.values(state.groups.items || {}), shallowEqual)
  const geofences = useSelector((state) => Object.values(state.geofences.items || {}), shallowEqual)
  const currentUser = useSelector((state) => state.session.user)
  const printing = useSelector((state) => state.session.printing)

  const [devices, setDevices] = useState([])
  const [users, setUsers] = useState([currentUser])
  const [deviceStatus, setDeviceStatus] = useState("")
  const [loading, setLoading] = useState(false)
  const [activeUsersStat, setActiveUsersStat] = useState([])
  const [activeDevicesStat, setActiveDevicesStat] = useState([])
  const [messagesReceivedStat, setMessagesReceivedStat] = useState([])
  const [statistics, setItemsStatistics] = useState({})
  const [deviceStatusChartData, setDeviceStatusChartData] = useState([])
  const [activeIndex, setActiveIndex] = useState(null)

  // Estados para nova tabela
  const [searchText, setSearchText] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const filteredDevices = useMemo(
    () => devices.filter((x) => (deviceStatus === "" ? x.status !== "never" : x.status.includes(deviceStatus))),
    [devices, deviceStatus],
  )

  // Filtro e busca para a tabela
  const filteredAndSearchedDevices = useMemo(() => {
    if (!searchText.trim()) {
      return filteredDevices
    }
    const lower = searchText.toLowerCase()
    return filteredDevices.filter((item) => {
      return (
        item.name?.toLowerCase().includes(lower) ||
        item.uniqueId?.toLowerCase().includes(lower) ||
        item.contact?.toLowerCase().includes(lower) ||
        item.phone?.toLowerCase().includes(lower) ||
        item.model?.toLowerCase().includes(lower) ||
        item.id?.toString().includes(lower)
      )
    })
  }, [filteredDevices, searchText])

  // Paginação
  const paginatedDevices = useMemo(() => {
    const start = page * rowsPerPage
    return filteredAndSearchedDevices.slice(start, start + rowsPerPage)
  }, [filteredAndSearchedDevices, page, rowsPerPage])

  const statCards = useMemo(
    () => [
      {
        icon: <GroupIcon color="primary" fontSize="medium" />,
        label: t("settingsUsers"),
        value: users.length,
      },
      {
        icon: <DevicesIcon color="primary" fontSize="medium" />,
        label: t("deviceTitle"),
        value: devices.length,
      },
      {
        icon: <Diversity3Icon color="primary" fontSize="medium" />,
        label: t("settingsGroups"),
        value: groups.length,
      },
      {
        icon: <TerrainIcon color="primary" fontSize="medium" />,
        label: t("sharedGeofences"),
        value: geofences.length,
      },
    ],
    [groups.length, users.length, devices.length, geofences.length, t],
  )

  const columns = useMemo(
    () => [
      {
        field: "photo",
        headerName: "",
        width: 60,
        disableColumnMenu: true,
        sortable: false,
        renderCell: (params) => (
          <Avatar alt={params.row.name} src={getDeviceIcon(params.row)} sx={{ width: 32, height: 32 }} />
        ),
      },
      { field: "name", headerName: t("sharedName"), flex: 1, sortable: false },
      { field: "uniqueId", headerName: t("deviceIdentifier"), flex: 1 },
      { field: "contact", headerName: t("sharedDevicePlate"), flex: 1 },
      { field: "phone", headerName: t("sharedPhone"), flex: 1 },
      { field: "model", headerName: t("deviceModel"), flex: 1 },
      {
        field: "deviceLastUpdate",
        headerName: t("deviceLastUpdate"),
        flex: 1,
        renderCell: (params) => (params.row.lastUpdate ? formatDate(params.row.lastUpdate) : ""),
      },
      {
        field: "ignition",
        headerName: t("positionIgnition"),
        flex: 1,
        renderCell: (params) => {
          const position = positions.find((p) => p.id === params.row.positionId)
          if (!position || !position.attributes) {
            return <Chip label={t("sharedNoData")} size="small" />
          }
          return position.attributes.ignition !== undefined ? (
            <Chip
              label={formatBoolean(position.attributes.ignition, t)}
              color={position.attributes.ignition ? "success" : "error"}
              size="small"
            />
          ) : <Chip label={t("sharedUnknown")} size="small" />
        },
      },
      {
        field: "eventAlarm",
        headerName: t("eventAlarm"),
        flex: 2,
        renderCell: (params) => {
          const position = positions.find((p) => p.id === params.row.positionId)
          if (!position || !position.attributes) {
            return <Chip label={t("sharedNoData")} size="small" />
          }
          return position.attributes.alarm ? (
            <Chip
              label={formatAlarm(position.attributes.alarm, t)}
              color="success"
              size="small"
            />
          ) : null
        },
      },
      {
        field: "positionBlocked",
        headerName: t("positionBlocked"),
        flex: 1,
        renderCell: (params) => {
          const position = positions.find((p) => p.id === params.row.positionId)
          if (!position) {
            return <Chip label={t("sharedNoData")} color="default" size="small" />
          }
          if (!position.attributes) {
            return <Chip label={t("sharedNoData")} color="warning" size="small" />
          }
          const blocked = position.attributes.blocked
          if (typeof blocked === "boolean") {
            return (
              <Tooltip title={blocked ? t("positionBlocked") : t("positionUnlocked")}>
                <Chip
                  icon={blocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                  label={blocked ? t("sharedYes") : t("sharedNo")}
                  color={blocked ? "error" : "success"}
                  size="small"
                />
              </Tooltip>
            )
          }
          return <Chip label={t("eventDeviceUnknown")} color="default" size="small" />
        },
      },
      {
        field: "deviceStatus",
        headerName: t("deviceStatus"),
        flex: 1,
        renderCell: (params) => {
          const status = params.row.status?.toLowerCase() || "unknown"
          const color = status === "online" ? "success" : status === "offline" ? "error" : "warning"
          return <Chip label={getStatusText(params.row.status || "unknown", t)} color={color} size="small" />
        },
      },
    ],
    [positions, t]
  )

  const getDeviceIcon = useCallback((item) => {
    if (item.photo && item.photo.length > 0 && item.photo.length < 30) {
      return `/photos/devices/${item.photo}`
    }
    return mapIcons[mapIconKey(item.category)] || "/placeholder.svg"
  }, [])

  const getAvatar = useCallback((item) => {
    let filephoto = "default.png"
    if (item.photo) {
      if (item.photo.preview) {
        return item.photo
      } else if (item.photo.length > 0 && item.photo.length < 30) {
        filephoto = item.photo
      }
    }
    return `./photos/users/${filephoto}`
  }, [])

  // Exportações
  const exportColumns = [
    { field: 'name', headerNameKey: 'sharedName' },
    { field: 'uniqueId', headerNameKey: 'deviceIdentifier' },
    { field: 'contact', headerNameKey: 'sharedDevicePlate' },
    { field: 'phone', headerNameKey: 'sharedPhone' },
    { field: 'model', headerNameKey: 'deviceModel' },
    {
      field: 'lastUpdate',
      headerNameKey: 'deviceLastUpdate',
      valueGetter: ({ row }) => formatDate(row.lastUpdate),
    },
    {
      field: 'status',
      headerNameKey: 'deviceStatus',
      valueGetter: ({ row }) => getStatusText(row.status || 'unknown', t),
    },
    {
      field: 'ignition',
      headerNameKey: 'positionIgnition',
      valueGetter: ({ row }) => {
        const position = positions.find((p) => p.id === row.positionId)
        return position?.attributes?.ignition !== undefined
          ? formatBoolean(position.attributes.ignition, t)
          : '-'
      },
    },
    {
      field: 'alarm',
      headerNameKey: 'eventAlarm',
      valueGetter: ({ row }) => {
        const position = positions.find((p) => p.id === row.positionId)
        return position?.attributes?.alarm ? formatAlarm(position.attributes.alarm, t) : '-'
      },
    },
    {
      field: 'blocked',
      headerNameKey: 'positionBlocked',
      valueGetter: ({ row }) => {
        const position = positions.find((p) => p.id === row.positionId)
        return position?.attributes?.blocked !== undefined
          ? formatBoolean(position.attributes.blocked, t)
          : '-'
      },
    },
  ]

  const { handleExportCsv, handleExportXlsx, handleExportPdf } = useCrudExports({
    titleKey: 'reportDashboardTitle',
    headerKey: 'reportDashboardHeader',
    rows: filteredAndSearchedDevices,
    columns: exportColumns,
    theme,
    t,
    options: {
      includeWatermark: true,
    },
  })

  // Filtros de status para os cards
  const statusFilters = useMemo(
    () => [
      {
        value: '',
        label: t('sharedAll'),
        count: devices.length,
        color: theme.palette.primary.main,
        Icon: StorageIcon,
      },
      {
        value: 'online',
        label: t('deviceStatusOnline'),
        count: devices.filter((d) => d.status === 'online').length,
        color: theme.palette.success.main,
        Icon: CheckCircleIcon,
      },
      {
        value: 'offline',
        label: t('deviceStatusOffline'),
        count: devices.filter((d) => d.status === 'offline').length,
        color: theme.palette.error.main,
        Icon: ErrorIcon,
      },
      {
        value: 'unknown',
        label: t('deviceStatusUnknown'),
        count: devices.filter((d) => d.status === 'unknown').length,
        color: theme.palette.warning.main,
        Icon: HelpIcon,
      },
    ],
    [devices, t, theme]
  )

  const fetchStatistics = useCallback(async () => {
    try {
      const selectedFrom = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      const selectedTo = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)

      const query = new URLSearchParams({
        from: selectedFrom.toISOString(),
        to: selectedTo.toISOString(),
      })

      const response = await fetch(`/api/statistics?${query.toString()}`, {
        Accept: "application/json",
      })

      if (response.ok) {
        const result = await response.json()
        const dailyData = result.map((entry) => {
          const date = new Date(entry.captureTime)
          const day = date.getDate().toString().padStart(2, "0")
          const month = (date.getMonth() + 1).toString().padStart(2, "0")
          return {
            name: `${day}/${month}`,
            activeUsers: entry.activeUsers,
            activeDevices: entry.activeDevices,
            messagesReceived: entry.messagesReceived,
          }
        })

        setActiveUsersStat(dailyData.map((item) => ({ name: item.name, users: item.activeUsers })))
        setActiveDevicesStat(dailyData.map((item) => ({ name: item.name, devices: item.activeDevices })))
        setMessagesReceivedStat(dailyData.map((item) => ({ name: item.name, messages: item.messagesReceived })))

        let activeUsers = 0
        let activeDevices = 0
        let messagesReceived = 0

        for (const stats of result) {
          activeUsers += stats.activeUsers
          activeDevices += stats.activeDevices
          messagesReceived += stats.messagesReceived
        }

        setItemsStatistics({
          activeUsers: Math.round(activeUsers / result.length),
          activeDevices: Math.round(activeDevices / result.length),
          messagesReceived,
        })
      }
    } catch (error) {
      console.error("Failed to fetch statistics:", error)
    }
  }, [])

  const fetchDevicesAndUsers = useCallback(async () => {
    setLoading(true)
    try {
      const devicesResponse = await fetchOrThrow("/api/devices")
      const deviceData = await devicesResponse.json()
      setDevices(deviceData)
      setDeviceStatusChartData([
        { id: 0, name: t("deviceStatusOnline"), value: deviceData.filter((d) => d.status === "online").length },
        { id: 1, name: t("deviceStatusOffline"), value: deviceData.filter((d) => d.status === "offline").length },
        { id: 2, name: t("deviceStatusUnknown"), value: deviceData.filter((d) => d.status === "unknown").length },
      ])

      if (admin || manager) {
        try {
          const usersResponse = await fetchOrThrow("/api/users?excludeAttributes=true")
          const userData = await usersResponse.json()
          setUsers(userData)
        } catch (error) {
          console.error("Failed to fetch users:", error)
        }
      }
    } catch (error) {
      console.error("Failed to fetch data:", error)
    } finally {
      setLoading(false)
    }
  }, [admin, manager, t])

  useEffectAsync(async () => {
    await fetchDevicesAndUsers()
    await fetchStatistics()
  }, [fetchDevicesAndUsers, fetchStatistics])

  if (printing) {
    return (
      <div style={{ color: "black", backgroundColor: "white" }}>
        <PrintHeader />
        <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ color: "black", fontWeight: "bold", textAlign: "center" }}>
            {t("dashboardMenuText")}
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {statCards.map((stat, index) => (
              <Grid key={index} sx={{ width: { xs: "100%", sm: "50%", md: "25%" } }}>
                <Card className={settingsClasses.statCard}>
                  <Box className={settingsClasses.statIcon}>{stat.icon}</Box>
                  <Box className={settingsClasses.statTextContainer}>
                    <Typography variant="body2">{stat.label}</Typography>
                    <Typography className={settingsClasses.statValue}>{stat.value}</Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {activeUsersStat.length > 0 && (
            <Card elevation={0} sx={{ p: 3, borderRadius: 3, marginBottom: "20px", border: "1px solid #ddd" }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "black", marginBottom: "10px" }}>
                {t("statisticsActiveUsers")}
              </Typography>
              <div style={{ height: "300px", width: "100%" }}>
                <ResponsiveContainer>
                  <LineChart data={activeUsersStat}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip />
                    <Line type="monotone" dataKey="users" stroke="#82ca9d" strokeWidth={3} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}

          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(50% - 8px)" } }}>
            <Card elevation={1} sx={{ p: 2, height: "100%" }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                {t("devicesAndState")}
              </Typography>
              <Box sx={{ height: 300 }}>
                {deviceStatusChartData.length > 0 ? (
                  <>
                    <PieChart
                      series={[{
                        data: deviceStatusChartData,
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        faded: {
                          innerRadius: 30,
                          additionalRadius: -30,
                          color: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300]
                        },
                      }]}
                      height={280}
                      slotProps={{
                        legend: { hidden: true },
                        tooltip: {
                          formatter: (params) => {
                            const total = deviceStatusChartData.reduce((sum, item) => sum + item.value, 0)
                            const percentage = total > 0 ? ((params.value / total) * 100).toFixed(1) : 0
                            return [
                              `${params.name}: ${params.value} (${percentage}%)`,
                              t('sharedTotal') + ': ' + total
                            ]
                          },
                        },
                      }}
                      colors={[theme.palette.success.main, theme.palette.error.main, theme.palette.warning.main]}
                    />
                    <CustomPieLegend data={deviceStatusChartData} theme={theme} />
                  </>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography variant="body1">{t("sharedLoading")}</Typography>
                  </Box>
                )}
              </Box>
            </Card>
          </Box>

          <Typography variant="h5" gutterBottom sx={{ color: "black", fontWeight: "bold", marginTop: "20px", marginBottom: "10px" }}>
            {t("deviceTitle")}
          </Typography>

          <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #ddd" }}>
            <thead>
              <tr>
                {columns.filter((col) => col.field !== "photo").map((column) => (
                  <th key={column.field} style={{ padding: "8px", backgroundColor: "#f2f2f2", color: "black", fontWeight: "bold", border: "1px solid #ddd", textAlign: "left" }}>
                    {column.headerName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredDevices.map((device) => (
                <tr key={device.id}>
                  {columns.filter((col) => col.field !== "photo").map((column) => {
                    let cellContent
                    if (column.renderCell) {
                      const rendered = column.renderCell({ row: device })
                      cellContent = React.isValidElements(rendered) ? (rendered.type === Chip ? rendered.props.label : "-") : rendered
                    } else {
                      cellContent = device[column.field] || "-"
                    }
                    return (
                      <td key={`${device.id}-${column.field}`} style={{ padding: "8px", border: "1px solid #ddd", color: "black" }}>
                        {cellContent}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Container>
      </div>
    )
  }

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={["settingsTitle", "dashboard"]}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2, height: "100%" }}>
        <Card sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1.5, minHeight: 56, flexWrap: "wrap", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", minWidth: 200 }}>
            <Avatar src={getAvatar(currentUser)} sx={{ width: 40, height: 40, mr: 1 }} />
            <Typography variant="body1" fontWeight="bold" noWrap>
              {currentUser.name}
            </Typography>
          </Box>

          <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} spacing={1} useFlexGap sx={{ flexGrow: 1, flexWrap: "wrap", justifyContent: "center", minWidth: 250, maxWidth: "100%" }}>
            <Typography variant="body2" noWrap>
              <strong>{t("userLimitCommands")}:</strong> {formatBoolean(userLimitCommands, t)}
            </Typography>
            <Typography variant="body2" noWrap>
              <strong>{t("userDeviceReadonly")}:</strong> {formatBoolean(userDeviceReadonly, t)}
            </Typography>
            <Typography variant="body2" noWrap>
              <strong>{t("serverReadonly")}:</strong> {formatBoolean(userReadonly, t)}
            </Typography>
          </Stack>
        </Card>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(1, 1fr)", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: 2, height: "auto" }}>
          {statCards.map((stat, index) => (
            <Card key={index} sx={{ p: 4, display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", minHeight: "auto", height: 80, boxShadow: 1, border: "1px solid", borderColor: "divider" }}>
              <Box sx={{ color: "primary.contrastText", p: 1.8, borderRadius: "50%", mr: 3, display: "flex", width: 48, height: 48, alignItems: "center", justifyContent: "center" }}>
                {React.cloneElement(stat.icon, { fontSize: "medium", sx: { fontSize: "60px" } })}
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, overflow: "hidden" }}>
                <Typography variant="body2" fontWeight={500} noWrap>{stat.label}</Typography>
                <Typography variant="h4" fontWeight={700}>{stat.value}</Typography>
              </Box>
            </Card>
          ))}
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(50% - 8px)" }, minWidth: 0 }}>
            <Card elevation={1} sx={{ p: 2, height: "100%" }}>
              <Typography variant="subtitle1" fontWeight="bold">{t("statisticsActiveUsers")}</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={activeUsersStat}
                  margin={{ top: 10, right: 20, left: -10, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                    axisLine={{ stroke: theme.palette.divider }}
                  />
                  <YAxis
                    domain={[0, 'dataMax']}
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                    axisLine={{ stroke: theme.palette.divider }}
                  />
                  <RechartsTooltip content={<CustomTooltip theme={theme} />} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="users"
                    name={t('statisticsActiveUsers')}
                    stroke="#2e7d32"
                    strokeWidth={2.5}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Box>

          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(50% - 8px)" }, minWidth: 0 }}>
            <Card elevation={1} sx={{ p: 2, height: "100%" }}>
              <Typography variant="subtitle1" fontWeight="bold">{t("statisticsMessagesReceived")}</Typography>
              {/* Use ResponsiveContainer with fixed height to avoid negative dimensions and improve readability */}
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={messagesReceivedStat}
                  margin={{ top: 10, right: 20, left: -10, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                    axisLine={{ stroke: theme.palette.divider }}
                  />
                  <YAxis
                    domain={[0, 'dataMax']}
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                    axisLine={{ stroke: theme.palette.divider }}
                  />
                  <RechartsTooltip content={<CustomTooltip theme={theme} />} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="messages"
                    name={t("statisticsMessagesReceived")}
                    stroke="#9c27b0"
                    strokeWidth={2.5}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6, fill: "#9c27b0" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Box>

          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(50% - 8px)" }, minWidth: 0 }}>
            <Card elevation={1} sx={{ p: 2, height: "100%" }}>
              <Typography variant="subtitle1" fontWeight="bold">{t("statisticsActiveDevices")}</Typography>
              {/* Use ResponsiveContainer with fixed height to avoid negative dimensions and improve readability */}
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={activeDevicesStat}
                  margin={{ top: 10, right: 20, left: -10, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                    axisLine={{ stroke: theme.palette.divider }}
                  />
                  <YAxis
                    domain={[0, 'dataMax']}
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                    axisLine={{ stroke: theme.palette.divider }}
                  />
                  <RechartsTooltip content={<CustomTooltip theme={theme} />} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="devices"
                    name={t("statisticsActiveDevices")}
                    stroke="#1976d2"
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: "#1976d2", stroke: "#fff", strokeWidth: 1 }}
                    activeDot={{ r: 6, fill: "#1976d2", stroke: "#fff", strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Box>

          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(50% - 8px)" } }}>
            <Card elevation={1} sx={{ p: 2, height: "100%" }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                {t("devicesAndState")}
              </Typography>
              <Box sx={{ height: 300 }}>
                {deviceStatusChartData.length > 0 ? (
                  <>
                    <PieChart
                      series={[{
                        data: deviceStatusChartData,
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        faded: {
                          innerRadius: 30,
                          additionalRadius: -30,
                          color: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300]
                        },
                      }]}
                      height={280}
                      slotProps={{
                        legend: { hidden: true },
                        tooltip: {
                          formatter: (params) => {
                            const total = deviceStatusChartData.reduce((sum, item) => sum + item.value, 0)
                            const percentage = total > 0 ? ((params.value / total) * 100).toFixed(1) : 0
                            return [
                              `${params.name}: ${params.value} (${percentage}%)`,
                              t('sharedTotal') + ': ' + total
                            ]
                          },
                        },
                      }}
                      colors={[theme.palette.success.main, theme.palette.error.main, theme.palette.warning.main]}
                    />
                    <CustomPieLegend data={deviceStatusChartData} theme={theme} />
                  </>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography variant="body1">{t("sharedLoading")}</Typography>
                  </Box>
                )}
              </Box>
            </Card>
          </Box>

          <Box sx={{ mt: 3, width: "100%" }}>
            {/* ═══════════════════════════════════════════════════════ */}
            {/* CARDS DE FILTRO DE STATUS */}
            {/* ═══════════════════════════════════════════════════════ */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              {statusFilters.map((filter) => (
                <Paper
                  key={filter.value}
                  onClick={() => {
                    setDeviceStatus(filter.value)
                    setPage(0)
                  }}
                  sx={{
                    flex: '1 1 200px',
                    minWidth: 200,
                    p: 1.5,
                    cursor: 'pointer',
                    border: 2,
                    borderColor: deviceStatus === filter.value
                      ? filter.color
                      : 'divider',
                    bgcolor: deviceStatus === filter.value
                      ? filter.color + '10'
                      : 'background.paper',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 3,
                      borderColor: filter.color,
                    },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        bgcolor: filter.color + '20',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <filter.Icon sx={{ color: filter.color, fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight="bold">
                        {filter.count}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {filter.label}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Box>

            {/* ═══════════════════════════════════════════════════════ */}
            {/* BARRA DE BUSCA E EXPORTAÇÕES */}
            {/* ═══════════════════════════════════════════════════════ */}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}
            >
              <TextField
                size="small"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value)
                  setPage(0)
                }}
                placeholder={t('sharedSearch')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{
                          color:
                            theme.palette.mode === 'dark'
                              ? theme.palette.text.secondary
                              : theme.palette.primary.dark,
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  flexGrow: 1,           // ⬅️ Faz o campo expandir até os botões
                  mr: 2,                 // ⬅️ Espaço entre campo e botões
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? theme.palette.grey[800]
                      : theme.palette.background.paper,
                  borderRadius: 1,
                }}
              />

              {manager && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    justifyContent: { xs: 'center', sm: 'flex-end' },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <Button variant="contained" color="csv" size="small" startIcon={<FileDownloadIcon />} onClick={handleExportCsv}>
                    CSV
                  </Button>
                  <Button variant="contained" color="excel" size="small" startIcon={<TableChartIcon />} onClick={handleExportXlsx}>
                    Excel
                  </Button>
                  <Button variant="contained" color="pdf" size="small" startIcon={<PictureAsPdfIcon />} onClick={handleExportPdf}>
                    PDF
                  </Button>
                </Stack>
              )}
            </Stack>

            {/* ═══════════════════════════════════════════════════════ */}
            {/* TABELA */}
            {/* ═══════════════════════════════════════════════════════ */}
            <Paper
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                overflow: 'hidden',
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          width: 60,
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.background.default
                              : theme.palette.primary.light,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {/* Avatar */}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.background.default
                              : theme.palette.primary.light,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t('sharedName')}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.background.default
                              : theme.palette.primary.light,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t('deviceIdentifier')}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.background.default
                              : theme.palette.primary.light,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t('sharedDevicePlate')}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.background.default
                              : theme.palette.primary.light,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t('sharedPhone')}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.background.default
                              : theme.palette.primary.light,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t('deviceModel')}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.background.default
                              : theme.palette.primary.light,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t('deviceLastUpdate')}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.background.default
                              : theme.palette.primary.light,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t('positionIgnition')}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.background.default
                              : theme.palette.primary.light,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t('eventAlarm')}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.background.default
                              : theme.palette.primary.light,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t('positionBlocked')}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          width: 120,
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.background.default
                              : theme.palette.primary.light,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t('deviceStatus')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : paginatedDevices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                          <Typography variant="body2" color="text.secondary">
                            {t('sharedNoData')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedDevices.map((row) => {
                        const position = positions.find((p) => p.id === row.positionId)
                        const status = row.status?.toLowerCase() || 'unknown'
                        const statusColor = status === 'online' ? 'success' : status === 'offline' ? 'error' : 'warning'

                        return (
                          <TableRow hover key={row.id}>
                            {/* Avatar */}
                            <TableCell>
                              <Avatar
                                alt={row.name}
                                src={getDeviceIcon(row)}
                                sx={{ width: 32, height: 32 }}
                              />
                            </TableCell>

                            {/* Nome */}
                            <TableCell>{row.name}</TableCell>

                            {/* ID Único */}
                            <TableCell>{row.uniqueId}</TableCell>

                            {/* Placa */}
                            <TableCell>{row.contact || '-'}</TableCell>

                            {/* Telefone */}
                            <TableCell>{row.phone || '-'}</TableCell>

                            {/* Modelo */}
                            <TableCell>{row.model || '-'}</TableCell>

                            {/* Última Atualização */}
                            <TableCell>
                              {row.lastUpdate ? formatDate(row.lastUpdate) : '-'}
                            </TableCell>

                            {/* Ignição */}
                            <TableCell>
                              {position?.attributes?.ignition !== undefined ? (
                                <Chip
                                  label={formatBoolean(position.attributes.ignition, t)}
                                  color={position.attributes.ignition ? 'success' : 'error'}
                                  size="small"
                                />
                              ) : (
                                <Chip label={t('sharedNoData')} size="small" />
                              )}
                            </TableCell>

                            {/* Alarme */}
                            <TableCell>
                              {position?.attributes?.alarm ? (
                                <Chip
                                  label={formatAlarm(position.attributes.alarm, t)}
                                  color="success"
                                  size="small"
                                />
                              ) : null}
                            </TableCell>

                            {/* Bloqueado */}
                            <TableCell>
                              {position?.attributes?.blocked !== undefined ? (
                                <Tooltip title={position.attributes.blocked ? t('positionBlocked') : t('positionUnlocked')}>
                                  <Chip
                                    icon={position.attributes.blocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                                    label={position.attributes.blocked ? t('sharedYes') : t('sharedNo')}
                                    color={position.attributes.blocked ? 'error' : 'success'}
                                    size="small"
                                  />
                                </Tooltip>
                              ) : (
                                <Chip label={t('sharedNoData')} color="default" size="small" />
                              )}
                            </TableCell>

                            {/* Status */}
                            <TableCell>
                              <Chip
                                label={getStatusText(row.status || 'unknown', t)}
                                color={statusColor}
                                size="small"
                                sx={{
                                  fontWeight: 500,
                                  fontSize: '0.75rem',
                                  height: 24,
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        )
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* ═══════════════════════════════════════════════════════ */}
              {/* PAGINAÇÃO */}
              {/* ═══════════════════════════════════════════════════════ */}
              <TablePagination
                component="div"
                count={filteredAndSearchedDevices.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10))
                  setPage(0)
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Por pág.:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`
                }
                sx={{
                  borderTop: `1px solid ${theme.palette.divider}`,
                  '.MuiTablePagination-select': {
                    width: '60px',
                    minWidth: '60px',
                    fontSize: '0.875rem',
                    paddingTop: '2px',
                    paddingBottom: '2px',
                    borderRadius: '2px',
                  },
                  '.MuiTablePagination-selectLabel': {
                    fontSize: '0.875rem',
                    margin: 0,
                    marginBottom: 0,
                  },
                  '.MuiTablePagination-displayedRows': {
                    fontSize: '0.875rem',
                    margin: 0,
                    marginBottom: 0,
                  },
                  '.MuiTablePagination-actions': {
                    marginLeft: '8px',
                    '& .MuiIconButton-root': {
                      padding: '4px',
                    },
                  },
                  '.MuiTablePagination-toolbar': {
                    minHeight: '52px',
                    paddingLeft: '16px',
                    paddingRight: '8px',
                  },
                }}
              />
            </Paper>
          </Box>
        </Box>
      </Box>
    </PageLayout>
  )
}

export default React.memo(DashboardPage)
